import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { fieldLabel, fieldWrapper, inputBase, selectBase } from '@/lib/form-styles'
import { apiErrorText } from '@/lib/api-error'
import { leseZahl } from '@/lib/speichern'
import { createPosition, fetchUnits } from '../api/calculation-positions.api'
import type { CalcGroup, CalcPosition, CalcUnit } from '../types/calculation.types'

/**
 * AP-37 Nachtrag 6 (N6-1) — **„+ Position" im Reiter Positionen.** Bisher entstand eine weitere Position nur
 * in der Positionsübersicht (leere letzte Zeile); wer am Blatt einer Position saß, musste dafür den Reiter
 * wechseln.
 *
 * Wie im Projektmanagement („+ Position", AP-36 4.2): nur, was zum Anlegen nötig ist — Menge, Einheit,
 * Bezeichnung, dazu die Gruppe, wenn die Kalkulation welche hat. Nummer und Reihenfolge vergibt der Server;
 * danach öffnet sich die neue Position mit ihrem Blatt.
 */
export function PositionAnlegenDialog({
  kalkulationId,
  gruppen,
  vorgabeGruppe,
  onClose,
  onAngelegt,
}: {
  kalkulationId: string
  /** Die Gruppen der Kalkulation, Eltern vor Kindern (`geordnet`). */
  gruppen: CalcGroup[]
  /** Gruppe der gerade geöffneten Position — die neue kommt ans Ende derselben Gruppe. */
  vorgabeGruppe: string | null
  onClose: () => void
  onAngelegt: (position: CalcPosition) => void
}) {
  const [menge, setMenge] = useState('')
  const [unitId, setUnitId] = useState('')
  const [text, setText] = useState('')
  const [groupId, setGroupId] = useState(vorgabeGruppe ?? '')
  const [einheiten, setEinheiten] = useState<CalcUnit[]>([])
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    fetchUnits().then(setEinheiten).catch(() => setEinheiten([]))
  }, [])

  // KA-49: keine vorgeschlagene Menge — leer gilt 1 (Platzhalter).
  const zahl = menge.trim() === '' ? 1 : leseZahl(menge)
  const mengeFalsch = zahl === null || Number.isNaN(zahl) || zahl < 0
  const bereit = text.trim() !== '' && !mengeFalsch && !laeuft

  async function anlegen() {
    if (!bereit) return
    setLaeuft(true)
    setFehler(null)
    try {
      onAngelegt(await createPosition(kalkulationId, { shortText: text.trim(), quantity: zahl as number, ...(unitId ? { unitId } : {}), ...(groupId ? { groupId } : {}) }))
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Position ließ sich nicht anlegen.'))
      setLaeuft(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <form
        className="w-full max-w-lg rounded-lg border border-border p-6 shadow-xl"
        style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
        onSubmit={(e) => {
          e.preventDefault()
          void anlegen()
        }}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
      >
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-semibold text-primary">Position anlegen</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>
        {fehler && <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}

        <div className="space-y-4">
          {/* Reihenfolge wie überall in der Kalkulation: Menge, Einheit, Bezeichnung („12 lfm Geländer"). */}
          <div className="grid gap-3 sm:grid-cols-[7rem_8rem_1fr]">
            <div className={fieldWrapper}>
              <label className={fieldLabel} htmlFor="kalk-pos-menge">
                Menge
              </label>
              <input
                id="kalk-pos-menge"
                className={`${inputBase} text-right`}
                inputMode="decimal"
                placeholder="1"
                autoFocus
                aria-invalid={mengeFalsch || undefined}
                value={menge}
                onChange={(e) => setMenge(e.target.value.replace(/[^0-9,.\s]/g, ''))}
              />
            </div>
            <div className={fieldWrapper}>
              <label className={fieldLabel} htmlFor="kalk-pos-einheit">
                Einheit
              </label>
              <select id="kalk-pos-einheit" className={selectBase} value={unitId} onChange={(e) => setUnitId(e.target.value)}>
                <option value="">—</option>
                {einheiten.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.code}
                  </option>
                ))}
              </select>
            </div>
            <div className={fieldWrapper}>
              <label className={fieldLabel} htmlFor="kalk-pos-text">
                Bezeichnung
              </label>
              <input id="kalk-pos-text" className={inputBase} maxLength={500} value={text} onChange={(e) => setText(e.target.value)} />
            </div>
          </div>
          {mengeFalsch && <p className="text-sm text-destructive">Die Menge ist keine gültige Zahl.</p>}

          {gruppen.length > 0 && (
            <div className={fieldWrapper}>
              <label className={fieldLabel} htmlFor="kalk-pos-gruppe">
                Gruppe
              </label>
              <select id="kalk-pos-gruppe" className={selectBase} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                <option value="">ohne Gruppe</option>
                {gruppen.map((g) => (
                  <option key={g.id} value={g.id}>
                    {'  '.repeat(g.level)}
                    {g.displayNumber} {g.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-4 py-2 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50" disabled={!bereit}>
            Anlegen
          </button>
        </div>
      </form>
    </div>
  )
}
